import React from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  ButtonVariant,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  JustifyContent,
  Link,
  Text,
} from '@gousto-internal/citrus-react'

import { RecipeCarouselCard } from './RecipeCarouselCard'
import { useRecipeCarouselNav } from './useRecipeCarouselNav'

export type RecipeCarouselRecipe = {
  id: string
  title: string
  imageUrl: string
  imageAlt: string
  cookTimeMins?: number
  kcal?: number
  dietaryClaims?: { slug: string }[]
  ariaLabel?: string
  onClick: () => void
}

export type RecipeCarouselHeaderAction = {
  label: string
  href?: string
  onClick: () => void
}

export type RecipeCarouselProps = {
  title: string
  subtitle?: string
  headerAction?: RecipeCarouselHeaderAction
  recipes: RecipeCarouselRecipe[]
  ariaLabel?: string
}

const HeaderAction = ({ action }: { action: RecipeCarouselHeaderAction }) => (
  <Box flexShrink={0} style={{ whiteSpace: 'nowrap' }}>
    {action.href ? (
      <Link href={action.href} onClick={action.onClick}>
        {action.label}
      </Link>
    ) : (
      <Link onClick={action.onClick}>{action.label}</Link>
    )}
  </Box>
)

export const RecipeCarousel = ({
  title,
  subtitle,
  headerAction,
  recipes,
  ariaLabel,
}: RecipeCarouselProps) => {
  const { emblaRef, nextBtnDisabled, onNextButtonClick, onPrevButtonClick, prevBtnDisabled } =
    useRecipeCarouselNav()

  return (
    <Box position={DisplayPosition.Relative}>
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.SpaceBetween}
        gap={3}
        paddingBottom={2}
      >
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} minWidth="0">
          <Body2 color={Color.ColdGrey_900} fontWeight={FontWeight.Bold} size={[5, 9]}>
            {title}
          </Body2>
          {subtitle ? (
            <Text color={Color.ColdGrey_600} size={1}>
              {subtitle}
            </Text>
          ) : null}
        </Box>
        {headerAction ? <HeaderAction action={headerAction} /> : null}
      </Box>

      <Box
        role="region"
        aria-roledescription="carousel"
        aria-label={ariaLabel ?? title}
        position={DisplayPosition.Relative}
      >
        <Box className="embla__viewport" ref={emblaRef} width="100%" style={{ overflow: 'hidden' }}>
          <Box
            alignItems={AlignItems.Stretch}
            className="embla__container"
            display={Display.Flex}
            gap={3}
            style={{ paddingBottom: '4px' }}
          >
            {recipes.map((recipe) => (
              <Box
                key={recipe.id}
                className="embla__slide"
                display={Display.Flex}
                flexGrow={0}
                flexShrink={0}
              >
                <RecipeCarouselCard recipe={recipe} />
              </Box>
            ))}
          </Box>
        </Box>

        <Button
          aria-label="Previous slide"
          disabled={prevBtnDisabled}
          colorVariant={ButtonColorVariant.Secondary}
          icon="arrow_back"
          onClick={onPrevButtonClick}
          size={ButtonSize.Large}
          style={{
            borderRadius: '50%',
            display: prevBtnDisabled ? 'none' : 'flex',
            position: 'absolute',
            bottom: '40%',
            left: '1%',
          }}
          variant={ButtonVariant.Icon}
        />
        <Button
          aria-label="Next slide"
          disabled={nextBtnDisabled}
          colorVariant={ButtonColorVariant.Secondary}
          icon="arrow_right"
          onClick={onNextButtonClick}
          size={ButtonSize.Large}
          style={{
            borderRadius: '50%',
            display: nextBtnDisabled ? 'none' : 'flex',
            position: 'absolute',
            bottom: '40%',
            right: '1%',
          }}
          variant={ButtonVariant.Icon}
        />
      </Box>
    </Box>
  )
}
